import { Injectable, OnDestroy, inject } from '@angular/core';
import { AccountSettingsService } from '@tc/account-settings';
import { ReplaySubject, Subject, Subscription } from 'rxjs';
import { first, shareReplay, tap } from 'rxjs/operators';
import { getSorts } from '../../common/utilities/get-sorts';
import { removeSubscriptions } from '../../common/utilities/remove-subscriptions';
import { AccountSettings } from '../../common/models/AccountSettings';
import { SortDirectionType } from '../../common/types/SortDirectionType';
import {
  BackorderPolicySortItemType,
  BuyingActivitySortItemType,
  MarketplaceSortItemType,
  PurchaseReceiptSortItemType,
  SearchSortItemType
} from '../../common/types/SortItemTypes';
import { ISortItem } from '../../common/models/ISortItem';
import { ItemObserver } from './item-update.observer';
import { ITableItem } from './models/ITableItem';
import { TableResult } from './models/TableResult';
import { TableSettings } from './models/TableSettings';

@Injectable()
export class TableService<T extends ITableItem> implements OnDestroy {
  private accountSettingsService = inject<AccountSettingsService<AccountSettings>>(AccountSettingsService);
  private itemObserver = inject<ItemObserver<T>>(ItemObserver);


  readonly tableSubject = new ReplaySubject<TableResult<T>>(1);
  readonly table$ = this.tableSubject.asObservable();

  private readonly itemsUpdatedSubject = new Subject<T[]>();
  readonly itemsUpdated$ = this.itemsUpdatedSubject.asObservable();

  private readonly isLoadingSubject = new ReplaySubject<boolean>(1);
  readonly isLoading$ = this.isLoadingSubject.asObservable();

  private tableSettings: TableSettings;

  private readonly tableLoadedSubject = new ReplaySubject<{
    page: number;
    size: number;
    sorts: ISortItem[];
  }>(1);
  readonly tableLoaded$ = this.tableLoadedSubject.asObservable().pipe(shareReplay(1));

  private tableSubscription: Subscription;
  private itemsUpdateSubscription: Subscription;
  private itemsRemovedSubscription: Subscription;

  constructor() {
    const tableSettings = inject(TableSettings);

    this.tableSettings = tableSettings;
    this.load(true);
    this.tableSubscription = this.accountSettingsService.settings$.subscribe(settings => {
      this.tableLoadedSubject.next({
        page: 1,
        size: <number>settings[this.tableSettings.pageSizeName],
        sorts: <ISortItem[]>settings[this.tableSettings.sortName] || []
      });
    });
    this.itemsUpdateSubscription = this.itemObserver.itemsUpdated$.subscribe(items =>
      this.updateTableItems(items)
    );
    this.itemsRemovedSubscription = this.itemObserver.itemsRemoved$.subscribe(ids =>
      this.removeTableItems(ids)
    );
  }

  ngOnDestroy() {
    removeSubscriptions([this.tableSubscription, this.itemsRemovedSubscription, this.itemsUpdateSubscription]);
  }

  setSorts(
    sortItemType:
      | SearchSortItemType
      | MarketplaceSortItemType
      | BuyingActivitySortItemType
      | PurchaseReceiptSortItemType
      | BackorderPolicySortItemType,
    sortDirectionType: SortDirectionType
  ): void {
    this.tableLoaded$.pipe(
      first(),
      tap(table => {
        const updatedSorts = getSorts(table.sorts, sortItemType, sortDirectionType);
        this.accountSettingsService.set(this.tableSettings.sortName, updatedSorts);
      })
    ).subscribe();
  }

  setCurrentPage(page: number): void {
    this.tableLoaded$.pipe(
      first(),
      tap(table => {
        this.tableLoadedSubject.next({
          page,
          size: table.size,
          sorts: table.sorts
        });
      })
    ).subscribe();
  }

  setPageSize(pageSize: number): void {
    this.accountSettingsService.set(this.tableSettings.pageSizeName, pageSize);
  }

  updateTableItems(newItems: T[]): void {
    this.itemsUpdatedSubject.next(newItems);
    this.table$.pipe(first()).subscribe(result => {
      const mergedItems = result.items.map(item => {
        const foundItem = newItems.find(i => i.name === item.name);
        if (foundItem) {
          const updatedItem = Object.assign(item, foundItem);
          return updatedItem;
        } else {
          return item;
        }
      });
      this.tableSubject.next({
        items: mergedItems,
        totalRecords: result.totalRecords
      });
    });
  }

  removeTableItems(ids: number[]): void {
    this.table$.pipe(first()).subscribe(result => {
      const originalCount = result.items.length;
      result.items = result.items.filter(i => !ids.includes(i.id));
      result.totalRecords =
        result.totalRecords - (originalCount - result.items.length);
      this.tableSubject.next(result);
    });
  }

  updateItem(item: T): void {
    this.itemObserver.updateItem(item);
  }

  load(isLoading: boolean) {
    this.isLoadingSubject.next(isLoading);
  }

  reloadTable(page?: number) {
    this.tableLoaded$.pipe(
      first(),
      tap(table => {
        this.tableLoadedSubject.next({
          page: page || 1,
          size: table.size,
          sorts: table.sorts
        });
      })
    ).subscribe();
  }
}
